import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectApplicationInfo } from "src/store/selectors";
import { isKeywordsDataSourceTypeGuard } from "src/utils";

/**
 * Hook to get the latest available keywords date for given data sources.
 * Returns the date in "YYYY-MM" format (e.g., "2025-11").
 *
 * This is used for determining the effectiveFrom date when applying
 * keyword/search changes to "current & future only".
 */
export const useLatestKeywordsDate = (
  keywordsDataSources: Search.KeywordsDataSource[],
): string | null => {
  const { keywordsSettings } = useSelector(selectApplicationInfo);

  return useMemo(() => {
    const dates: string[] = [];

    for (const keywordsDataSource of keywordsDataSources) {
      if (!isKeywordsDataSourceTypeGuard(keywordsDataSource)) continue;

      const settings = keywordsSettings[keywordsDataSource];
      if (settings?.date) {
        dates.push(settings.date); // Already in "YYYY-MM" format
      }
    }

    if (dates.length === 0) return null;

    dates.sort();
    return dates[dates.length - 1] || null;
  }, [keywordsDataSources, keywordsSettings]);
};
